import React from "react";
import styled from "styled-components";

import LanguageSwitcher from "../../../components/tab/LanguageSwitcher";
import { pickLang } from "../utils";
import type { ProfileFooter, ProfileLanguage } from "../../../types/profile";

export interface ProfileFooterSectionProps {
  /** フッターブロック。未設定（null）でも既定コピーライトで描画する。 */
  footer: ProfileFooter | null;
  language: ProfileLanguage;
  /** 言語切替（Profile の setLanguage をそのまま受ける）。 */
  onChangeLanguage: React.Dispatch<React.SetStateAction<ProfileLanguage>>;
}

/** Django 既定のコピーライト（footer.copyright が空のとき表示）。 */
const DEFAULT_COPYRIGHT = "©SOREMO Co., ltd. All right reserved";

/** ソーシャル種別 → Material Symbols のアイコン名（ブランドグリフは無いため代替）。 */
const SOCIAL_ICON: Record<string, string> = {
  home: "public",
  twitter: "alternate_email",
  fb: "group",
  insta: "photo_camera",
  youtube: "smart_display",
  tiktok: "music_note",
  note: "edit_note",
};

/** 全幅の薄グレー背景（Django .profile-footer の #fcfcfc）。 */
const FooterRoot = styled.footer`
  width: 100%;
  min-height: 100px;
  background: #fcfcfc;
`;

const FooterContainer = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
`;

const Divider = styled.hr`
  width: 100%;
  margin: 16px 0;
  border: none;
  border-top: 1px solid var(--soremo-border);
`;

/**
 * フッターセクション（Django プロフィールシート準拠）。
 *
 * 上段: メインメニュー（左）/ ソーシャルリンク（右）。中央に区切り線。
 * 下段: コピーライト + サブメニュー（左）/ 言語切替（右、グローバル
 * LanguageSwitcher）。メニュー等が空でも、既定コピーライト・区切り線・言語
 * 切替は常に表示する。
 */
const ProfileFooterSection: React.FC<ProfileFooterSectionProps> = ({
  footer,
  language,
  onChangeLanguage,
}) => {
  const menus = footer?.menus ?? [];
  const socials = footer?.socials ?? [];
  const copyright = footer?.copyright?.trim() || DEFAULT_COPYRIGHT;

  const mainMenus = menus.filter((m) => m.type_footer === "1").sort((a, b) => a.order - b.order);
  const subMenus = menus.filter((m) => m.type_footer === "2").sort((a, b) => a.order - b.order);
  const sortedSocials = [...socials].sort((a, b) => a.order - b.order);

  return (
    <FooterRoot>
      <FooterContainer>
        {(mainMenus.length > 0 || sortedSocials.length > 0) && (
          <div className="u-row-between u-w100" style={{ alignItems: "flex-end", gap: 16 }}>
            <nav className="u-row" style={{ flexWrap: "wrap", gap: "8px 32px" }}>
              {mainMenus.map((m) => (
                <a
                  key={m.id}
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bodytext-13"
                >
                  {pickLang(m.title_jp, m.title_en, language)}
                </a>
              ))}
            </nav>
            {sortedSocials.length > 0 && (
              <div className="u-row" style={{ gap: 12 }}>
                {sortedSocials.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.type_social_link}
                    className="u-text-light-gray"
                  >
                    <span className="material-symbols-rounded">
                      {SOCIAL_ICON[s.type_social_link] || "link"}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        <Divider />

        <div className="u-row-between u-w100" style={{ gap: 16 }}>
          <div className="u-row" style={{ flexWrap: "wrap", gap: "4px 32px" }}>
            <span className="bodytext-11 u-text-light-gray">{copyright}</span>
            {subMenus.map((m) => (
              <a
                key={m.id}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bodytext-11 u-text-light-gray"
              >
                {pickLang(m.title_jp, m.title_en, language)}
              </a>
            ))}
          </div>
          <div style={{ width: 24, flexShrink: 0 }}>
            <LanguageSwitcher onLanguageChange={(lang: ProfileLanguage) => onChangeLanguage(lang)} />
          </div>
        </div>
      </FooterContainer>
    </FooterRoot>
  );
};

export default ProfileFooterSection;
